import { useEffect, useId, useState } from 'react'
import { SegmentedControl } from '@/components/SegmentedControl'
import {
  SegmentedIndicator,
  segmentedOptionClassName,
} from '@/components/SegmentedIndicator'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRange } from '@/components/ui/date-range-picker.types'
import { parseUtcDate } from '@/lib/performanceUrl'
import type { PerformanceDateRangeProps } from './PerformanceDateRange.types'

const PRESETS = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '1y', label: '1 year' },
] as const

// Local, not UTC, components: preserves the calendar day the picker showed regardless of the host timezone offset.
function toDateOnlyString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Mirrors toDateOnlyString: builds a local-midnight Date so the picker renders the same calendar day back.
function toLocalDate(value: string): Date | undefined {
  const utc = parseUtcDate(value)
  if (!utc) return undefined

  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate())
}

export function PerformanceDateRange({
  range,
  from,
  to,
  onRangeChange,
  onApplyCustom,
}: PerformanceDateRangeProps) {
  const indicatorId = useId()
  const [isCustomOpen, setIsCustomOpen] = useState(range === 'custom')

  useEffect(() => {
    setIsCustomOpen(range === 'custom')
  }, [range])

  function handlePreset(preset: (typeof PRESETS)[number]['value']) {
    setIsCustomOpen(false)
    onRangeChange(preset)
  }

  function handlePickerChange(picked: DateRange | undefined) {
    if (!picked?.from || !picked?.to) return
    onApplyCustom({
      from: toDateOnlyString(picked.from),
      to: toDateOnlyString(picked.to),
    })
  }

  const pickerValue: DateRange | undefined =
    from && to ? { from: toLocalDate(from), to: toLocalDate(to) } : undefined

  return (
    <fieldset className="flex flex-wrap items-end justify-end gap-4 ml-auto">
      <legend className="sr-only">Date range</legend>

      <SegmentedControl label="Date range">
        {PRESETS.map((preset) => {
          const active = !isCustomOpen && range === preset.value

          return (
            <label
              key={preset.value}
              className={segmentedOptionClassName(active)}
            >
              {active && <SegmentedIndicator layoutId={indicatorId} />}
              <input
                type="radio"
                name={indicatorId}
                value={preset.value}
                checked={active}
                onChange={() => handlePreset(preset.value)}
                className="sr-only"
              />
              <span className="relative">{preset.label}</span>
            </label>
          )
        })}

        <label className={segmentedOptionClassName(isCustomOpen)}>
          {isCustomOpen && <SegmentedIndicator layoutId={indicatorId} />}
          <input
            type="radio"
            name={indicatorId}
            value="custom"
            checked={isCustomOpen}
            onChange={() => setIsCustomOpen(true)}
            className="sr-only"
          />
          <span className="relative">Custom</span>
        </label>
      </SegmentedControl>

      {isCustomOpen && (
        <DateRangePicker
          value={pickerValue}
          onChange={handlePickerChange}
          placeholder="Select date range"
        />
      )}
    </fieldset>
  )
}
